import { api } from './api'

export interface IntegrationType {
  id?: number
  name: string
  enabled: boolean
  status?: string
  integrator_id?: number
  integrator?: IntegratorType
  frequency: 'once' | 'every day at 8am' | 'every hour'
  reprocessing_days: number
  mapping?: string | object
  credentials?: any[]
  tag?: string
  created_at?: Date
  updated_at?: Date
  last_date?: Date
  last_process_status?: number
  next_process?: Date
  authorization_id?: number
  last_error_message?: string
  last_error_category?: string
}

export interface IntegratorType {
  id: number
  name: string
  slug: string
  segment: string
  technology: string
  description?: string
  mapping?: string | object
}

export interface IntegrationsResponse {
  integrations: IntegrationType[]
  total: number
  page: number
  size: number
}

export interface CollectionStatsType {
  integration_id: number
  status: number
  step: string
  recs: number
  last_date: string | null
  last_recs: number
}

export interface CollectionStatsResponse {
  collection_stats: CollectionStatsType[]
}

export interface ActiveBackfillDetails {
  job_id: number
  integration_id: number
  status: 'running' | 'pending'
  period_start: string | null
  period_end: string | null
  created_at: string | null
  total_periods: number
  completed_periods: number
  failed_periods: number
  progress_pct: number
}

export interface ActiveBackfillResponse {
  active_backfill: ActiveBackfillDetails | null
}

class IntegrationsService {
  private listRequests = new Map<string, Promise<IntegrationsResponse>>()

  async list(
    page = 1,
    size = 30,
    sortField?: string,
    sortOrder?: string,
    search?: string,
    statusFilter?: number[]
  ): Promise<IntegrationsResponse> {
    // Create a unique key for this request to enable deduplication
    const statusKey = statusFilter ? statusFilter.sort().join(',') : ''
    const requestKey = `${page}-${size}-${sortField || ''}-${sortOrder || ''}-${search || ''}-${statusKey}`

    // Check if this exact request is already in progress
    if (this.listRequests.has(requestKey)) {
      return await this.listRequests.get(requestKey)!
    }
    const requestPromise = this._fetchIntegrations(page, size, sortField, sortOrder, search, statusFilter)
    this.listRequests.set(requestKey, requestPromise)

    try {
      const result = await requestPromise
      return result
    } finally {
      this.listRequests.delete(requestKey)
    }
  }

  private async _fetchIntegrations(
    page = 1,
    size = 30,
    sortField?: string,
    sortOrder?: string,
    search?: string,
    statusFilter?: number[]
  ): Promise<IntegrationsResponse> {
    const params: any = { page, size }

    // Add search parameter if provided
    if (search && search.trim()) {
      params.search = search.trim()
    }

    // Add status filter if provided (as separate params for FastAPI compatibility)
    if (statusFilter && statusFilter.length > 0) {
      // Don't add to params object - we'll append manually to URL
    }

    // Map frontend sort fields to backend database fields
    const sortFieldMapping: { [key: string]: string } = {
      'id': 'id',
      'name': 'name',
      'integrator': 'integrator',  // Backend now supports via JOIN
      'segment': 'segment',        // Backend now supports via JOIN
      'status': 'status',
      'enabled': 'enabled',
      'last_date': 'last_date',
      'created_at': 'created_at',
      'updated_at': 'updated_at'
    }

    const validSortOrders = ['asc', 'desc']

    // Map the frontend field to backend field
    if (sortField && sortFieldMapping[sortField]) {
      params.sort_field = sortFieldMapping[sortField]
    }
    if (sortOrder && validSortOrders.includes(sortOrder)) {
      params.sort_order = sortOrder
    }

    // Get organization slug from URL for organization-scoped API calls
    const orgSlug = this.getOrgSlugFromUrl()
    let integrationPath = orgSlug ? `/${orgSlug}/integrations/` : '/integrations/'

    // Manually append status filter params (FastAPI expects status=0&status=7 format)
    if (statusFilter && statusFilter.length > 0) {
      const statusParams = statusFilter.map(s => `status=${s}`).join('&')
      integrationPath += `?${statusParams}`
    }

    try {
      const response = await api.get(integrationPath, { params })
      return response.data
    } catch (error: any) {
      // If sorting causes 500 error, try without sorting parameters
      if (error.response?.status === 500 && (sortField || sortOrder)) {
        console.warn('Sorting parameters caused server error, retrying without sorting')
        const basicParams: any = { page, size }
        if (search && search.trim()) basicParams.search = search.trim()
        // Rebuild path with status filter
        let retryPath = orgSlug ? `/${orgSlug}/integrations/` : '/integrations/'
        if (statusFilter && statusFilter.length > 0) {
          const statusParams = statusFilter.map(s => `status=${s}`).join('&')
          retryPath += `?${statusParams}`
        }
        const response = await api.get(retryPath, { params: basicParams })
        return response.data
      }
      throw error
    }
  }

  private getOrgSlugFromUrl(): string | null {
    const pathname = window.location.pathname
    const parts = pathname.substring(1).split('/')
    // Check if first part looks like an org slug (UUID-like)
    if (parts[0] && parts[0].length > 0 && /^[a-f0-9]{8}$/.test(parts[0])) {
      return parts[0]
    }
    return null
  }

  async show(integrationId: number): Promise<IntegrationType> {
    const orgSlug = this.getOrgSlugFromUrl()
    const integrationPath = orgSlug ? `/${orgSlug}/integrations/${integrationId}` : `/integrations/${integrationId}`
    const response = await api.get(integrationPath)
    return response.data
  }

  async update(integrationId: number, data: Partial<IntegrationType>): Promise<IntegrationType> {
    const orgSlug = this.getOrgSlugFromUrl()
    const integrationPath = orgSlug ? `/${orgSlug}/integrations/${integrationId}` : `/integrations/${integrationId}`
    const response = await api.put(integrationPath, data)
    return response.data.integration
  }

  async create(data: Partial<IntegrationType>): Promise<IntegrationType> {
    const orgSlug = this.getOrgSlugFromUrl()
    const integrationPath = orgSlug ? `/${orgSlug}/integrations/` : '/integrations/'
    const response = await api.post(integrationPath, data)
    return response.data.integration
  }

  async delete(integrationId: number): Promise<void> {
    const orgSlug = this.getOrgSlugFromUrl()
    const integrationPath = orgSlug ? `/${orgSlug}/integrations/${integrationId}` : `/integrations/${integrationId}`
    await api.delete(integrationPath)
  }

  async setStatus(integrationId: number, enabled: boolean): Promise<IntegrationType> {
    const orgSlug = this.getOrgSlugFromUrl()
    const integrationPath = orgSlug ? `/${orgSlug}/integrations/${integrationId}` : `/integrations/${integrationId}`
    const response = await api.put(integrationPath, { enabled })
    return response.data.integration
  }

  async test(integrationId: number): Promise<any> {
    const orgSlug = this.getOrgSlugFromUrl()
    const testPath = orgSlug ? `/${orgSlug}/integrations/test-credentials` : '/integrations/test-credentials'
    const response = await api.post(testPath, {
      integration_id: integrationId
    })
    return response.data
  }

  async backfill(integrationId: number, startDate: string, endDate: string): Promise<any> {
    const orgSlug = this.getOrgSlugFromUrl()
    const backfillPath = orgSlug ? `/${orgSlug}/integrations/${integrationId}/backfill` : `/integrations/${integrationId}/backfill`
    const response = await api.post(backfillPath, {
      start_date: startDate,
      end_date: endDate
    })
    return response.data
  }

  async getActiveBackfill(integrationId: number): Promise<ActiveBackfillResponse> {
    const orgSlug = this.getOrgSlugFromUrl()
    const path = orgSlug
      ? `/${orgSlug}/integrations/${integrationId}/active-backfill`
      : `/integrations/${integrationId}/active-backfill`
    const response = await api.get(path)
    return response.data
  }

  async cancelBackfill(integrationId: number): Promise<{ status: string; message: string; job_id?: number }> {
    const orgSlug = this.getOrgSlugFromUrl()
    const path = orgSlug
      ? `/${orgSlug}/integrations/${integrationId}/backfill`
      : `/integrations/${integrationId}/backfill`
    const response = await api.delete(path)
    return response.data
  }

  async oauthCallback(state: string, code: string): Promise<any> {
    // OAuth callback is global, not organization-specific
    const response = await api.post('/oauth/callback', {
      code: code,
      state: state
    })
    return response
  }

  async getCollectionStats(): Promise<CollectionStatsResponse> {
    const orgSlug = this.getOrgSlugFromUrl()
    const statsPath = orgSlug ? `/${orgSlug}/integrations/collection-stats` : '/integrations/collection-stats'
    const response = await api.get(statsPath)
    return response.data
  }

  async getCollectionStatsForIntegration(integrationId: number): Promise<{ collection_stats: CollectionStatsType | null }> {
    const orgSlug = this.getOrgSlugFromUrl()
    const statsPath = orgSlug
      ? `/${orgSlug}/integrations/${integrationId}/collection-stats`
      : `/integrations/${integrationId}/collection-stats`
    const response = await api.get(statsPath)
    return response.data
  }

  async getPushIntegrations(): Promise<{push_integrations: {id: number}[]}> {
    const orgSlug = this.getOrgSlugFromUrl()
    const pushPath = orgSlug ? `/${orgSlug}/integrations/push-integrations` : '/integrations/push-integrations'
    const response = await api.get(pushPath)
    return response.data
  }

  async getAllTags(): Promise<{tags: string[]}> {
    const orgSlug = this.getOrgSlugFromUrl()
    const tagsPath = orgSlug ? `/${orgSlug}/integrations/tags` : '/integrations/tags'
    const response = await api.get(tagsPath)
    return response.data
  }

  async deleteTag(tag: string): Promise<{deleted_count: number}> {
    const orgSlug = this.getOrgSlugFromUrl()
    const tagsPath = orgSlug ? `/${orgSlug}/integrations/tags/${encodeURIComponent(tag)}` : `/integrations/tags/${encodeURIComponent(tag)}`
    const response = await api.delete(tagsPath)
    return response.data
  }

  async getCredentialErrorCount(): Promise<number> {
    // Status 0 = credential_error only (not pending_credentials)
    // Filter for enabled integrations only
    const orgSlug = this.getOrgSlugFromUrl()
    const integrationPath = orgSlug ? `/${orgSlug}/integrations/` : '/integrations/'

    const response = await api.get(integrationPath, { params: { page: 1, size: 1, status: 0, enabled: true } })
    return response.data.total
  }

}

export const integrationsService = new IntegrationsService()